import React from 'react';
import {
  Typography,
  Table,
  Tag,
  Space,
  Button,
  Spin,
  Alert,
  DatePicker,
  message,
  Modal,
  Checkbox,
  Tabs,
} from 'antd';
import { Pie, Column, Line } from '@ant-design/plots';
import MobileTable from '../../Component/MobileTable';
import { AnimatedContainer, StatCard, SectionCard, EmptyState } from '../../Component/ui';
import type { StatCardTone, StatCardVariant } from '../../Component/ui';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import moment from 'moment';
import dayjs, { Dayjs } from 'dayjs';
import 'dayjs/locale/ar';
import {
  ShoppingOutlined,
  UserOutlined,
  ShoppingCartOutlined,
  DollarOutlined,
  EyeOutlined,
  ReloadOutlined,
  DownloadOutlined,
  BarChartOutlined,
  LineChartOutlined,
  RiseOutlined,
} from '@ant-design/icons';
import { useDashboardStatistics } from '../../Hooks/useDashboardStatistics';
import { useSalesByRepresentatives } from '../../Hooks/useSalesByRepresentatives';
import { usePermissions } from '../../contexts/PermissionContext';
import { useSelector } from 'react-redux';
import { RootState } from '../../redux/root-reducer';
import axios from 'axios';
import SalesRepPaymentPercentage from '../../Component/SalesRepPaymentPercentage';
import {
  totalPaymentPercentage,
  hasInvoicesInPeriod,
} from '../../utils/salesRepresentativePayment';
import './style.scss';

const { Text } = Typography;
const { RangePicker } = DatePicker;

const CHART_PALETTE = [
  '#6366f1',
  '#06b6d4',
  '#10b981',
  '#d946ef',
  '#f97316',
  '#f43f5e',
  '#0ea5e9',
  '#a855f7',
];

const Dashboard: React.FC = () => {
  const { t, i18n } = useTranslation();
  const history = useHistory();
  const { hasAnyPermission } = usePermissions();
  const { userInfo } = useSelector((state: RootState) => state.setReducer);
  const { stats, recentOrders, monthlyStats, chartData, loading, error } = useDashboardStatistics();
  const [chartReady, setChartReady] = React.useState(false);
  const chartContainerRef = React.useRef<HTMLDivElement>(null);

  // Get current date range with correct dayjs instances (Ant Design v5 uses dayjs)
  const getInitialDateRange = () => {
    const start = dayjs().startOf('month');
    const end = dayjs().endOf('month');
    return [start, end] as [Dayjs, Dayjs];
  };

  // Date range state for sales by representatives chart (using Dayjs for Ant Design)
  const [dateRange, setDateRange] =
    React.useState<[Dayjs | null, Dayjs | null]>(getInitialDateRange());

  const [startDate, setStartDate] = React.useState<string>(
    moment().startOf('month').format('YYYY-MM-DD')
  );
  const [endDate, setEndDate] = React.useState<string>(
    moment().endOf('month').format('YYYY-MM-DD')
  );

  // Modal state for viewing/exporting reports
  const [isViewModalVisible, setIsViewModalVisible] = React.useState(false);
  const [selectedRepresentatives, setSelectedRepresentatives] = React.useState<number[]>([]);
  const [exportLoading, setExportLoading] = React.useState(false);
  const [activeTab, setActiveTab] = React.useState<string>('');
  const [representativeDetails, setRepresentativeDetails] = React.useState<{
    [key: number]: any[];
  }>({});
  const [loadingDetails, setLoadingDetails] = React.useState<{ [key: number]: boolean }>({});
  const [actualChartColors, setActualChartColors] = React.useState<{ [key: string]: string }>({});

  // Permission checks
  const canViewProducts = hasAnyPermission(['view_products', 'view_product', 'view_dashboard']);
  const canViewCustomers = hasAnyPermission(['view_customers', 'view_customer', 'view_dashboard']);
  const canViewOrders = hasAnyPermission(['view_orders', 'view_order', 'view_dashboard']);
  /** Total sales figure — financial dashboard access, not the sales-by-rep chart */
  const canViewSales = hasAnyPermission(['view_financial_reports', 'view_dashboard_sales_reports']);
  /** Sales-by-representative chart and preview modal on the home dashboard */
  const canViewSalesReports = hasAnyPermission(['view_dashboard_sales_reports']);
  /** Export from dashboard preview modal only */
  const canExportDashboardReports = hasAnyPermission(['export_dashboard_sales_reports']);
  const canOpenSalesReportModal = canViewSalesReports || canExportDashboardReports;
  const canViewInvoices = hasAnyPermission(['view_invoices', 'view_invoice', 'view_dashboard']);
  const canViewReceipts = hasAnyPermission(['view_receipts', 'view_receipt', 'view_dashboard']);
  const canViewVisits = hasAnyPermission(['view_visits', 'view_visit', 'view_dashboard']);

  const formatChartAmount = React.useCallback(
    (value: number) =>
      new Intl.NumberFormat(i18n.language === 'ar' ? 'ar-SA' : 'en-US', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(value),
    [i18n.language]
  );

  const monthlyChartData = React.useMemo(() => {
    const locale = i18n.language === 'ar' ? 'ar' : 'en';
    return (chartData?.monthlySales ?? []).map((item) => ({
      month: item.month,
      monthLabel: dayjs(`${item.month}-01`).locale(locale).format('MMM'),
      sales: Number(item.paid_amount ?? 0),
      invoices: Number(item.invoice_count ?? 0),
    }));
  }, [chartData?.monthlySales, i18n.language]);

  const activityPieData = React.useMemo(() => {
    const invoices = monthlyStats.invoices.current;
    const receipts = monthlyStats.receipts.current;
    if (invoices === 0 && receipts === 0) {
      return [];
    }
    return [
      { type: t('dashboard.thisMonthInvoices'), value: invoices },
      { type: t('dashboard.thisMonthReceipts'), value: receipts },
    ];
  }, [monthlyStats.invoices.current, monthlyStats.receipts.current, t]);

  const monthlyReceiptsData = React.useMemo(() => {
    const locale = i18n.language === 'ar' ? 'ar' : 'en';
    return (chartData?.monthlyReceipts ?? []).map((item) => ({
      month: item.month,
      monthLabel: dayjs(`${item.month}-01`).locale(locale).format('MMM'),
      amount: Number(item.amount ?? 0),
      count: Number(item.receipt_count ?? 0),
    }));
  }, [chartData?.monthlyReceipts, i18n.language]);

  const ordersByStatusData = React.useMemo(
    () =>
      (chartData?.ordersByStatus ?? []).map((item) => ({
        type: t(`dashboard.orderStatus.${item.status}`, item.status),
        value: Number(item.count ?? 0),
      })),
    [chartData?.ordersByStatus, t]
  );

  const visitsByStatusData = React.useMemo(
    () =>
      (chartData?.visitsByStatus ?? []).map((item) => ({
        type: t(`dashboard.visitStatus.${item.status}`, item.status),
        value: Number(item.count ?? 0),
      })),
    [chartData?.visitsByStatus, t]
  );

  const monthlyVisitsData = React.useMemo(() => {
    const locale = i18n.language === 'ar' ? 'ar' : 'en';
    return (chartData?.monthlyVisits ?? []).map((item) => ({
      month: item.month,
      monthLabel: dayjs(`${item.month}-01`).locale(locale).format('MMM'),
      visits: Number(item.visit_count ?? 0),
    }));
  }, [chartData?.monthlyVisits, i18n.language]);

  const showOverviewCharts = canViewSales || canViewInvoices;
  const showActivityCharts =
    canViewOrders || canViewVisits || (canViewReceipts && monthlyReceiptsData.length > 0);

  // Fetch sales by representatives data (only if user has permission)
  const {
    data: salesByRepData,
    loading: salesByRepLoading,
    error: salesByRepError,
    refetch: refetchSalesByRep,
  } = useSalesByRepresentatives(startDate, endDate, canViewSalesReports, 'dashboard');

  // Ensure chart container is ready before rendering
  React.useEffect(() => {
    setChartReady(false);
    // Small delay to ensure DOM is ready and has dimensions
    const timer = setTimeout(() => {
      if (chartContainerRef.current) {
        const rect = chartContainerRef.current.getBoundingClientRect();
        // Only set ready if container has actual dimensions
        if (rect.width > 0 && rect.height > 0) {
          setChartReady(true);
        } else {
          // Retry after a short delay if dimensions aren't ready
          setTimeout(() => {
            if (chartContainerRef.current) {
              const retryRect = chartContainerRef.current.getBoundingClientRect();
              if (retryRect.width > 0 && retryRect.height > 0) {
                setChartReady(true);
              }
            }
          }, 200);
        }
      }
    }, 150);
    return () => clearTimeout(timer);
  }, [salesByRepData, i18n.language, startDate, endDate]);

  // Apply Arabic legend styles after chart renders
  React.useEffect(() => {
    if (i18n.language === 'ar' && chartReady) {
      const applyLegendStyles = () => {
        const legendList = document.querySelector('.chart-section .g2-legend-list');
        if (legendList) {
          const items = legendList.querySelectorAll('.g2-legend-list-item');
          items.forEach((item: Element) => {
            const htmlItem = item as HTMLElement;
            htmlItem.style.margin = '0';
            htmlItem.style.padding = '4px 0';
            htmlItem.style.marginLeft = '0';
            htmlItem.style.marginRight = '0';
            htmlItem.style.maxWidth = 'none';
            htmlItem.style.whiteSpace = 'nowrap';

            const textElement = htmlItem.querySelector('.g2-legend-list-item-text') as HTMLElement;
            if (textElement) {
              textElement.style.whiteSpace = 'nowrap';
              textElement.style.overflow = 'visible';
              textElement.style.textOverflow = 'clip';
              textElement.style.maxWidth = 'none';
              textElement.style.width = 'auto';
            }
          });
        }
      };

      // Apply immediately and after a delay
      applyLegendStyles();
      const timer = setTimeout(applyLegendStyles, 300);
      const timer2 = setTimeout(applyLegendStyles, 600);

      return () => {
        clearTimeout(timer);
        clearTimeout(timer2);
      };
    }
  }, [chartReady, i18n.language, salesByRepData]);

  if (loading) {
    return (
      <div className="dashboard-page">
        <div style={{ textAlign: 'center', padding: '50px' }}>
          <Spin size="large" />
          <div style={{ marginTop: '16px' }}>{t('common.loading')}</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="dashboard-page">
        <Alert
          message="Error"
          description={error}
          type="error"
          showIcon
          style={{ margin: '24px' }}
        />
      </div>
    );
  }

  const statsData = [
    {
      title: t('dashboard.totalProducts'),
      value: stats.totalProducts,
      prefix: <ShoppingOutlined />,
      icon: 'shopping',
      type: 'products' as const,
      tone: 'primary' as StatCardTone,
    },
    {
      title: t('dashboard.totalCustomers'),
      value: stats.totalCustomers,
      prefix: <UserOutlined />,
      icon: 'user',
      type: 'customers' as const,
      tone: 'violet' as StatCardTone,
    },
    {
      title: t('dashboard.totalOrders'),
      value: stats.totalOrders,
      prefix: <ShoppingCartOutlined />,
      icon: 'shopping-cart',
      type: 'orders' as const,
      tone: 'info' as StatCardTone,
    },
    {
      title: t('dashboard.totalSales'),
      value: `${stats.totalSales} ${i18n.language === 'en' ? 'SAR' : 'ر.س'}`,
      prefix: <DollarOutlined />,
      icon: 'dollar',
      type: 'sales' as const,
      tone: 'success' as StatCardTone,
    },
  ];

  const columns = [
    {
      title: t('orders.columns.id'),
      dataIndex: 'orderId',
      key: 'orderId',
      render: (text: string) => <Text strong>{text}</Text>,
    },
    {
      title: t('orders.columns.customer'),
      dataIndex: 'customer',
      key: 'customer',
    },
    {
      title: t('orders.columns.total'),
      dataIndex: 'amount',
      key: 'amount',
      render: (text: string) => (
        <Text strong style={{ color: '#10b981' }}>
          {text} {i18n.language === 'en' ? 'SAR' : 'ر.س'}
        </Text>
      ),
    },
    {
      title: t('orders.columns.status'),
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => {
        const statusMap: { [key: string]: { color: string; text: string } } = {
          approved: { color: 'green', text: t('orders.status.approved') },
          pending: { color: 'orange', text: t('orders.status.pending') },
          cancelled: { color: 'red', text: t('orders.status.cancelled') },
          paid: { color: 'blue', text: t('orders.status.approved') },
          'partially paid': { color: 'purple', text: t('orders.status.pending') },
          draft: { color: 'default', text: t('orders.status.draft') },
          completed: { color: 'green', text: t('orders.status.completed') },
          processing: { color: 'cyan', text: t('orders.status.processing') },
        };
        const statusInfo = statusMap[status.toLowerCase()] || { color: 'default', text: status };
        return (
          <Tag color={statusInfo.color} className="status-tag">
            {statusInfo.text}
          </Tag>
        );
      },
    },
    {
      title: t('orders.columns.createdAt'),
      dataIndex: 'date',
      key: 'date',
    },
    {
      title: t('orders.columns.actions'),
      key: 'actions',
      render: (record: any) => (
        <Space size="middle">
          <Button
            type="link"
            icon={<EyeOutlined />}
            size="small"
            onClick={() => handleViewOrderDetails(record)}
          >
            {t('common.view')}
          </Button>
        </Space>
      ),
    },
  ];

  const getCurrentDate = () => {
    return new Date().toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  // Handle navigation to orders page
  const handleViewAllOrders = () => {
    history.push('/orders');
  };

  // Handle viewing order details
  const handleViewOrderDetails = (record: any) => {
    if (record.id) {
      history.push(`/orders/${record.id}`);
    } else if (record.orderId) {
      history.push(`/orders/${record.orderId}`);
    }
  };

  // Handle date range change for sales by representatives
  const handleDateRangeChange = (
    dates: [Dayjs | null, Dayjs | null] | null,
    _dateStrings: [string, string]
  ) => {
    if (dates && dates[0] && dates[1]) {
      // Convert dayjs to moment for formatting (we use moment internally)
      const startMoment = moment(dates[0].format('YYYY-MM-DD'));
      const endMoment = moment(dates[1].format('YYYY-MM-DD'));

      // Verify the dates are valid and years are reasonable (1900-2100)
      if (startMoment.isValid() && endMoment.isValid()) {
        const startYear = startMoment.year();
        const endYear = endMoment.year();

        // Check if years are reasonable (fix common parsing errors)
        if (startYear >= 1900 && startYear <= 2100 && endYear >= 1900 && endYear <= 2100) {
          setDateRange([dates[0], dates[1]]);
          setStartDate(startMoment.format('YYYY-MM-DD'));
          setEndDate(endMoment.format('YYYY-MM-DD'));
        } else {
          console.warn('Invalid year detected:', { startYear, endYear });
          message.warning('Please select a valid date range (years between 1900-2100)');
        }
      } else {
        console.warn('Invalid dates:', { dates });
      }
    }
  };

  // Handle refresh button click
  const handleRefreshChart = () => {
    refetchSalesByRep(startDate, endDate);
  };

  // Handle view button click - open modal
  const handleViewReport = () => {
    if (!canOpenSalesReportModal) {
      message.error(
        t('dashboard.salesReportForbidden', 'You do not have permission to view this report.')
      );
      return;
    }
    if (!startDate || !endDate) {
      message.warning(t('dashboard.selectDateRange'));
      return;
    }
    if (!salesByRepData || salesByRepData.sales_by_representatives.length === 0) {
      message.warning(t('dashboard.noDataAvailable'));
      return;
    }
    // Select all representatives by default
    const allIds = salesByRepData.sales_by_representatives.map(
      (rep: any) => rep.sales_representative_id
    );
    setSelectedRepresentatives(allIds);
    if (allIds.length > 0 && canViewSalesReports) {
      setActiveTab(String(allIds[0]));
      loadRepresentativeDetails(allIds[0]);
    } else {
      setActiveTab('');
    }
    setIsViewModalVisible(true);
  };

  // Load details for a specific representative
  const loadRepresentativeDetails = async (representativeId: number) => {
    if (representativeDetails[representativeId]) {
      return; // Already loaded
    }

    setLoadingDetails((prev) => ({ ...prev, [representativeId]: true }));

    try {
      const token = localStorage.getItem('token');
      const baseURL = process.env.REACT_APP_API_URL || 'https://driveshield.net';
      const apiBaseURL = baseURL.startsWith('http') ? baseURL : `http://${baseURL}`;

      const response = await axios.get(
        `${apiBaseURL}/api/admin/dashboard/sales-representative-details`,
        {
          params: {
            representative_id: representativeId,
            start_date: moment(startDate).format('YYYY-MM-DD'),
            end_date: moment(endDate).format('YYYY-MM-DD'),
            permission_context: 'dashboard',
          },
          headers: {
            Authorization: token ? `Bearer ${token}` : '',
          },
        }
      );

      if (response.data && response.data.status && response.data.data) {
        setRepresentativeDetails((prev) => ({
          ...prev,
          [representativeId]: response.data.data,
        }));
      }
    } catch (error: any) {
      console.error('Error loading representative details:', error);
      message.error(error.response?.data?.message || t('common.error'));
    } finally {
      setLoadingDetails((prev) => ({ ...prev, [representativeId]: false }));
    }
  };

  // Handle tab change
  const handleTabChange = (key: string) => {
    setActiveTab(key);
    const representativeId = parseInt(key, 10);
    if (!isNaN(representativeId)) {
      loadRepresentativeDetails(representativeId);
    }
  };

  // Handle export button click from modal
  const handleExportReport = async (representativeIds?: number[]) => {
    if (!canExportDashboardReports) {
      message.error(
        t(
          'salesRepresentatives.salesAnalytics.downloadForbidden',
          'You do not have permission to download this report.'
        )
      );
      return;
    }
    if (!startDate || !endDate) {
      message.warning(t('dashboard.selectDateRange'));
      return;
    }

    const idsToExport = representativeIds || selectedRepresentatives;
    if (idsToExport.length === 0) {
      message.warning('Please select at least one representative');
      return;
    }

    setExportLoading(true);
    const formattedStartDate = moment(startDate).format('YYYY-MM-DD');
    const formattedEndDate = moment(endDate).format('YYYY-MM-DD');

    try {
      const token = localStorage.getItem('token');
      const baseURL = process.env.REACT_APP_API_URL || 'https://driveshield.net';
      const apiBaseURL = baseURL.startsWith('http') ? baseURL : `http://${baseURL}`;

      const response = await axios.get(
        `${apiBaseURL}/api/admin/dashboard/export-sales-by-representatives`,
        {
          params: {
            start_date: formattedStartDate,
            end_date: formattedEndDate,
            representative_ids: idsToExport.join(','),
            permission_context: 'dashboard',
          },
          responseType: 'blob',
          headers: {
            Authorization: token ? `Bearer ${token}` : '',
          },
        }
      );

      if (response.data && response.data instanceof Blob) {
        const contentType = response.headers['content-type'] || '';

        if (contentType.includes('application/json')) {
          const text = await response.data.text();
          const errorData = JSON.parse(text);
          throw new Error(errorData.message || errorData.error || 'Export failed');
        }

        const blob = new Blob([response.data], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        });
        const url = globalThis.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `sales_by_representatives_${formattedStartDate}_to_${formattedEndDate}.xlsx`;
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        link.remove();
        globalThis.URL.revokeObjectURL(url);

        message.success(t('dashboard.exportSuccess'));
        setIsViewModalVisible(false);
      } else {
        throw new Error('Invalid response format from export API');
      }
    } catch (error: any) {
      console.error('Export error:', error);

      if (error.response && error.response.data instanceof Blob) {
        try {
          const errorText = await error.response.data.text();
          const errorData = JSON.parse(errorText);
          message.error(errorData.message || errorData.error || t('dashboard.exportError'));
        } catch (parseError) {
          message.error(error.message || t('dashboard.exportError'));
        }
      } else if (error.response && error.response.data && typeof error.response.data === 'string') {
        try {
          const errorData = JSON.parse(error.response.data);
          message.error(errorData.message || errorData.error || t('dashboard.exportError'));
        } catch {
          message.error(error.message || t('dashboard.exportError'));
        }
      } else {
        message.error(error.message || t('dashboard.exportError'));
      }
    } finally {
      setExportLoading(false);
    }
  };

  // Handle checkbox change in modal
  const handleSelectAll = (checked: boolean) => {
    if (checked && salesByRepData) {
      setSelectedRepresentatives(
        salesByRepData.sales_by_representatives.map((rep: any) => rep.sales_representative_id)
      );
    } else {
      setSelectedRepresentatives([]);
    }
  };

  const handleSelectRepresentative = (id: number, checked: boolean) => {
    if (checked) {
      setSelectedRepresentatives([...selectedRepresentatives, id]);
    } else {
      setSelectedRepresentatives(selectedRepresentatives.filter((repId) => repId !== id));
    }
  };

  const statVariants: StatCardVariant[] = ['bold', 'bold', 'bold', 'bold'];

  return (
    <AnimatedContainer className="dashboard-page" variant="fadeUp">
      <section className="dashboard-hero">
        <div className="dashboard-hero__mesh" aria-hidden />
        <div className="dashboard-hero__orbs" aria-hidden>
          <span className="dashboard-hero__orb dashboard-hero__orb--1" />
          <span className="dashboard-hero__orb dashboard-hero__orb--2" />
          <span className="dashboard-hero__orb dashboard-hero__orb--3" />
        </div>
        <div className="dashboard-hero__content">
          <div className="dashboard-hero__top">
            <div className="dashboard-hero__badge">
              <span className="dashboard-hero__live-dot" />
              {t('dashboard.liveDashboard', 'Live Dashboard')}
            </div>
            <Text className="dashboard-hero__date">{getCurrentDate()}</Text>
          </div>
          <h1 className="dashboard-hero__title">
            {t('dashboard.welcomeMessage', {
              name: userInfo?.user?.name ?? '',
            })}
          </h1>
          <p className="dashboard-hero__subtitle">{t('dashboard.overview')}</p>
          <div className="dashboard-hero__actions">
            {canViewOrders && (
              <Button
                className="dashboard-hero__pill"
                icon={<ShoppingCartOutlined />}
                onClick={() => history.push('/orders')}
              >
                {t('navigation.orders', 'Orders')}
              </Button>
            )}
            {canViewProducts && (
              <Button
                className="dashboard-hero__pill"
                icon={<ShoppingOutlined />}
                onClick={() => history.push('/products')}
              >
                {t('navigation.products', 'Products')}
              </Button>
            )}
            {canViewCustomers && (
              <Button
                className="dashboard-hero__pill"
                icon={<UserOutlined />}
                onClick={() => history.push('/customers')}
              >
                {t('navigation.customers', 'Customers')}
              </Button>
            )}
            {canViewSalesReports && (
              <Button
                className="dashboard-hero__pill dashboard-hero__pill--accent"
                icon={<BarChartOutlined />}
                onClick={() => history.push('/reports/sales')}
              >
                {t('navigation.reports', 'Reports')}
              </Button>
            )}
          </div>
        </div>
      </section>

      {/* Statistics Cards */}
      <div className="stats-grid ui-stagger-children">
        {statsData.map((stat, index) => {
          // Check permission for each stat card
          let canView = false;
          if (stat.type === 'products') {
            canView = canViewProducts;
          } else if (stat.type === 'customers') {
            canView = canViewCustomers;
          } else if (stat.type === 'orders') {
            canView = canViewOrders;
          } else if (stat.type === 'sales') {
            canView = canViewSales;
          }

          if (!canView) {
            return null;
          }

          return (
            <StatCard
              key={index}
              title={stat.title}
              value={stat.value}
              icon={stat.prefix}
              tone={stat.tone}
              variant={statVariants[index % statVariants.length]}
              delay={index * 0.08}
            />
          );
        })}
      </div>

      {/* Overview charts (monthly sales / invoices / activity) — temporarily hidden */}
      {false && showOverviewCharts && (
        <div className="dashboard-charts ui-stagger-children">
          {canViewSales && (
            <SectionCard
              title={t('dashboard.monthlySalesTrend')}
              icon={<RiseOutlined />}
              hoverable
              delay={0.1}
              className="dashboard-chart-card dashboard-chart-card--revenue"
              extra={
                <Text type="secondary" className="dashboard-chart-card__meta">
                  {monthlyChartData.length} {t('dashboard.dataPoints')}
                </Text>
              }
            >
              {loading ? (
                <div className="dashboard-chart-card__loading">
                  <Spin />
                </div>
              ) : monthlyChartData.length > 0 ? (
                <Column
                  data={monthlyChartData}
                  xField="monthLabel"
                  yField="sales"
                  height={280}
                  color="#5b5ef7"
                  columnStyle={{ radius: [8, 8, 0, 0] }}
                  meta={{
                    sales: { alias: t('dashboard.revenue') },
                    monthLabel: { alias: t('dashboard.thisMonth') },
                  }}
                  xAxis={{
                    label: {
                      autoRotate: true,
                      style: { fill: '#64748b', fontSize: 12 },
                    },
                  }}
                  yAxis={{
                    label: {
                      formatter: (value: string) => formatChartAmount(Number(value)),
                      style: { fill: '#64748b', fontSize: 12 },
                    },
                    grid: { line: { style: { stroke: '#e2e8f0', lineDash: [4, 4] } } },
                  }}
                />
              ) : (
                <EmptyState
                  size="small"
                  title={t('dashboard.noDataAvailable')}
                  description={t('dashboard.monthlySalesTrend')}
                />
              )}
            </SectionCard>
          )}

          {canViewInvoices && (
            <SectionCard
              title={t('dashboard.monthlyInvoiceTrend')}
              icon={<LineChartOutlined />}
              hoverable
              delay={0.15}
              className="dashboard-chart-card dashboard-chart-card--invoices"
              extra={
                <Text type="secondary" className="dashboard-chart-card__meta">
                  {monthlyChartData.length} {t('dashboard.dataPoints')}
                </Text>
              }
            >
              {loading ? (
                <div className="dashboard-chart-card__loading">
                  <Spin />
                </div>
              ) : monthlyChartData.length > 0 ? (
                <Line
                  data={monthlyChartData}
                  xField="monthLabel"
                  yField="invoices"
                  height={280}
                  smooth
                  color="#10b981"
                  areaStyle={{
                    fill: 'l(270) 0:rgba(16,185,129,0.35) 1:rgba(16,185,129,0.02)',
                  }}
                  point={{
                    size: 5,
                    shape: 'circle',
                    style: { fill: '#fff', stroke: '#059669', lineWidth: 2 },
                  }}
                  lineStyle={{ lineWidth: 3 }}
                  meta={{
                    invoices: { alias: t('dashboard.invoiceCount') },
                    monthLabel: { alias: t('dashboard.thisMonth') },
                  }}
                  xAxis={{
                    label: {
                      autoRotate: true,
                      style: { fill: '#64748b', fontSize: 12 },
                    },
                  }}
                  yAxis={{
                    label: {
                      style: { fill: '#64748b', fontSize: 12 },
                    },
                    grid: { line: { style: { stroke: '#e2e8f0', lineDash: [4, 4] } } },
                  }}
                />
              ) : (
                <EmptyState
                  size="small"
                  title={t('dashboard.noDataAvailable')}
                  description={t('dashboard.monthlyInvoiceTrend')}
                />
              )}
            </SectionCard>
          )}

          {canViewInvoices && canViewReceipts && (
            <SectionCard
              title={t('dashboard.monthlyActivityChart')}
              icon={<BarChartOutlined />}
              hoverable
              delay={0.2}
              className="dashboard-chart-card dashboard-chart-card--activity dashboard-chart-card--compact"
            >
              {loading ? (
                <div className="dashboard-chart-card__loading">
                  <Spin />
                </div>
              ) : activityPieData.length > 0 ? (
                <Pie
                  data={activityPieData}
                  angleField="value"
                  colorField="type"
                  height={280}
                  radius={0.9}
                  innerRadius={0.62}
                  legend={{ position: 'bottom' }}
                  color={['#5b5ef7', '#10b981']}
                  label={{
                    position: 'spider',
                    text: (datum: any) => {
                      const total = activityPieData.reduce(
                        (sum: number, item: any) => sum + (item.value || 0),
                        0
                      );
                      const pct = total ? (((datum?.value || 0) / total) * 100).toFixed(1) : '0';
                      return `${datum?.type || ''}  ${pct}%`;
                    },
                  }}
                  statistic={{
                    title: {
                      content: t('dashboard.thisMonth'),
                      style: { fontSize: '13px', color: '#64748b' },
                    },
                    content: {
                      content: formatChartAmount(
                        monthlyStats.invoices.current + monthlyStats.receipts.current
                      ),
                      style: { fontSize: '22px', fontWeight: 700, color: 'var(--color-heading)' },
                    },
                  }}
                />
              ) : (
                <EmptyState
                  size="small"
                  title={t('dashboard.noDataAvailable')}
                  description={t('dashboard.monthlyActivityChart')}
                />
              )}
            </SectionCard>
          )}
        </div>
      )}

      {/* Activity charts (orders / visits / receipts) — temporarily hidden */}
      {false && showActivityCharts && (
        <div className="dashboard-charts ui-stagger-children">
          {canViewOrders && (
            <SectionCard
              title={t('dashboard.ordersByStatus')}
              icon={<ShoppingCartOutlined />}
              hoverable
              delay={0.1}
              className="dashboard-chart-card dashboard-chart-card--revenue"
            >
              {loading ? (
                <div className="dashboard-chart-card__loading">
                  <Spin />
                </div>
              ) : ordersByStatusData.length > 0 ? (
                <Column
                  data={ordersByStatusData}
                  xField="type"
                  yField="value"
                  height={280}
                  color="#6366f1"
                  columnStyle={{ radius: [8, 8, 0, 0] }}
                  xAxis={{ label: { autoRotate: true, style: { fill: '#64748b', fontSize: 12 } } }}
                  yAxis={{
                    label: { style: { fill: '#64748b', fontSize: 12 } },
                    grid: { line: { style: { stroke: '#e2e8f0', lineDash: [4, 4] } } },
                  }}
                />
              ) : (
                <EmptyState
                  size="small"
                  title={t('dashboard.noDataAvailable')}
                  description={t('dashboard.ordersByStatus')}
                />
              )}
            </SectionCard>
          )}

          {canViewVisits && (
            <SectionCard
              title={t('dashboard.visitsByStatus')}
              icon={<EyeOutlined />}
              hoverable
              delay={0.15}
              className="dashboard-chart-card dashboard-chart-card--activity dashboard-chart-card--compact"
            >
              {loading ? (
                <div className="dashboard-chart-card__loading">
                  <Spin />
                </div>
              ) : visitsByStatusData.length > 0 ? (
                <Pie
                  data={visitsByStatusData}
                  angleField="value"
                  colorField="type"
                  height={280}
                  radius={0.9}
                  innerRadius={0.62}
                  legend={{ position: 'bottom' }}
                  color={CHART_PALETTE}
                  label={{
                    position: 'spider',
                    text: (datum: any) => {
                      const total = visitsByStatusData.reduce(
                        (sum: number, item: any) => sum + (item.value || 0),
                        0
                      );
                      const pct = total ? (((datum?.value || 0) / total) * 100).toFixed(1) : '0';
                      return `${datum?.type || ''}  ${pct}%`;
                    },
                  }}
                />
              ) : (
                <EmptyState
                  size="small"
                  title={t('dashboard.noDataAvailable')}
                  description={t('dashboard.visitsByStatus')}
                />
              )}
            </SectionCard>
          )}

          {canViewVisits && (
            <SectionCard
              title={t('dashboard.monthlyVisitsTrend')}
              icon={<BarChartOutlined />}
              hoverable
              delay={0.2}
              className="dashboard-chart-card dashboard-chart-card--invoices"
              extra={
                <Text type="secondary" className="dashboard-chart-card__meta">
                  {monthlyVisitsData.length} {t('dashboard.dataPoints')}
                </Text>
              }
            >
              {loading ? (
                <div className="dashboard-chart-card__loading">
                  <Spin />
                </div>
              ) : monthlyVisitsData.length > 0 ? (
                <Column
                  data={monthlyVisitsData}
                  xField="monthLabel"
                  yField="visits"
                  height={280}
                  color="#06b6d4"
                  columnStyle={{ radius: [8, 8, 0, 0] }}
                  meta={{
                    visits: { alias: t('dashboard.visitCount') },
                    monthLabel: { alias: t('dashboard.thisMonth') },
                  }}
                  xAxis={{ label: { autoRotate: true, style: { fill: '#64748b', fontSize: 12 } } }}
                  yAxis={{
                    label: { style: { fill: '#64748b', fontSize: 12 } },
                    grid: { line: { style: { stroke: '#e2e8f0', lineDash: [4, 4] } } },
                  }}
                />
              ) : (
                <EmptyState
                  size="small"
                  title={t('dashboard.noDataAvailable')}
                  description={t('dashboard.monthlyVisitsTrend')}
                />
              )}
            </SectionCard>
          )}

          {canViewReceipts && (
            <SectionCard
              title={t('dashboard.monthlyReceiptsTrend')}
              icon={<LineChartOutlined />}
              hoverable
              delay={0.25}
              className="dashboard-chart-card dashboard-chart-card--invoices"
              extra={
                <Text type="secondary" className="dashboard-chart-card__meta">
                  {monthlyReceiptsData.length} {t('dashboard.dataPoints')}
                </Text>
              }
            >
              {loading ? (
                <div className="dashboard-chart-card__loading">
                  <Spin />
                </div>
              ) : monthlyReceiptsData.length > 0 ? (
                <Line
                  data={monthlyReceiptsData}
                  xField="monthLabel"
                  yField="amount"
                  height={280}
                  smooth
                  color="#10b981"
                  areaStyle={{ fill: 'l(270) 0:rgba(16,185,129,0.35) 1:rgba(16,185,129,0.02)' }}
                  point={{
                    size: 5,
                    shape: 'circle',
                    style: { fill: '#fff', stroke: '#059669', lineWidth: 2 },
                  }}
                  lineStyle={{ lineWidth: 3 }}
                  meta={{
                    amount: { alias: t('dashboard.totalReceipts') },
                    monthLabel: { alias: t('dashboard.thisMonth') },
                  }}
                  xAxis={{ label: { autoRotate: true, style: { fill: '#64748b', fontSize: 12 } } }}
                  yAxis={{
                    label: {
                      formatter: (value: string) => formatChartAmount(Number(value)),
                      style: { fill: '#64748b', fontSize: 12 },
                    },
                    grid: { line: { style: { stroke: '#e2e8f0', lineDash: [4, 4] } } },
                  }}
                />
              ) : (
                <EmptyState
                  size="small"
                  title={t('dashboard.noDataAvailable')}
                  description={t('dashboard.monthlyReceiptsTrend')}
                />
              )}
            </SectionCard>
          )}
        </div>
      )}

      {/* Content Grid */}
      <div className="content-grid">
        {/* Recent Orders */}
        {canViewOrders && (
          <SectionCard
            title={t('dashboard.recentOrders')}
            icon={<ShoppingCartOutlined />}
            hoverable
            delay={0.15}
            extra={
              <Button type="link" style={{ padding: 0 }} onClick={handleViewAllOrders}>
                {t('common.viewAll')}
              </Button>
            }
            className="recent-orders"
            bodyClassName="recent-orders-body"
          >
            {recentOrders && recentOrders.length > 0 ? (
              <MobileTable
                columns={columns}
                dataSource={recentOrders}
                pagination={false}
                rowKey="id"
                size="small"
                onRowClick={(record: any) => handleViewOrderDetails(record)}
              />
            ) : (
              <EmptyState
                size="small"
                title={t('dashboard.noDataAvailable')}
                description={t('dashboard.recentOrders')}
              />
            )}
          </SectionCard>
        )}

        {/* Sales Chart - Sales by Representatives */}
        {canViewSalesReports && (
          <SectionCard
            title={t('dashboard.salesChart')}
            icon={<DollarOutlined />}
            hoverable
            delay={0.2}
            className="chart-section"
            extra={
              <Space>
                <RangePicker
                  value={dateRange}
                  onChange={handleDateRangeChange}
                  format="YYYY-MM-DD"
                  style={{ marginRight: 8 }}
                  placeholder={['Start Date', 'End Date']}
                  allowClear={false}
                  disabledDate={(current: Dayjs | null) => {
                    if (!current) return false;
                    const today = dayjs().endOf('day');
                    // Disable dates after today
                    if (current.isAfter(today)) {
                      return true;
                    }
                    // Disable dates before year 1900
                    if (current.year() < 1900) {
                      return true;
                    }
                    // Disable dates after year 2100
                    if (current.year() > 2100) {
                      return true;
                    }
                    return false;
                  }}
                />
                <Button
                  icon={<ReloadOutlined />}
                  onClick={handleRefreshChart}
                  loading={salesByRepLoading}
                >
                  {t('common.refresh')}
                </Button>
                {canOpenSalesReportModal && (
                  <Button
                    type="primary"
                    icon={canViewSalesReports ? <EyeOutlined /> : <DownloadOutlined />}
                    onClick={handleViewReport}
                    disabled={
                      !salesByRepData || salesByRepData.sales_by_representatives.length === 0
                    }
                  >
                    {canViewSalesReports ? t('common.view') : t('common.export')}
                  </Button>
                )}
              </Space>
            }
          >
            {salesByRepLoading ? (
              <div style={{ textAlign: 'center', padding: '40px 20px' }}>
                <Spin size="large" />
              </div>
            ) : salesByRepError ? (
              <Alert
                message={t('common.error')}
                description={salesByRepError}
                type="error"
                showIcon
              />
            ) : salesByRepData && salesByRepData.sales_by_representatives.length > 0 ? (
              <div
                ref={chartContainerRef}
                style={{
                  height: '400px',
                  minHeight: '400px',
                  width: '100%',
                  position: 'relative',
                }}
              >
                {(() => {
                  try {
                    // Prepare data for pie chart
                    const filteredData = salesByRepData.sales_by_representatives.filter(
                      (item: any) => item && item.sales_representative_name && item.total_sales > 0
                    );

                    const defaultColors = CHART_PALETTE;

                    // Create color mapping for each representative based on original order
                    const colorMap: { [key: string]: string } = {};
                    filteredData.forEach((item: any, index: number) => {
                      const name = item.sales_representative_name || 'Unknown';
                      colorMap[name] = defaultColors[index % defaultColors.length];
                    });

                    // Map to pie chart data format
                    const pieData = filteredData.map((item: any) => ({
                      type: item.sales_representative_name || 'Unknown',
                      value: Number(item.total_sales) || 0,
                    }));

                    if (pieData.length === 0) {
                      return (
                        <div
                          style={{
                            textAlign: 'center',
                            padding: '40px 20px',
                            color: 'var(--color-text-secondary)',
                          }}
                        >
                          {t('dashboard.noDataAvailable')}
                        </div>
                      );
                    }

                    if (!chartReady || !chartContainerRef.current) {
                      return (
                        <div
                          style={{
                            textAlign: 'center',
                            padding: '40px 20px',
                            color: 'var(--color-text-secondary)',
                          }}
                        >
                          {t('common.loading')}
                        </div>
                      );
                    }

                    const containerWidth = chartContainerRef.current.clientWidth;
                    if (!containerWidth || containerWidth === 0) {
                      return (
                        <div
                          style={{
                            textAlign: 'center',
                            padding: '40px 20px',
                            color: 'var(--color-text-secondary)',
                          }}
                        >
                          {t('common.loading')}
                        </div>
                      );
                    }

                    // Create color array in the same order as pieData
                    const colorArray = pieData.map(
                      (item: any) => colorMap[item.type] || defaultColors[0]
                    );

                    // Calculate legend height based on number of items
                    // Arabic text is wider, so show 3 per row, English can show 4 per row
                    const itemsPerRow = i18n.language === 'ar' ? 3 : 4;
                    const legendRows = Math.ceil(pieData.length / itemsPerRow);
                    const legendHeight = Math.max(60, legendRows * 28);

                    // Configure pie chart
                    const config: any = {
                      appendPadding: [10, 10, legendHeight + 20, 10],
                      data: pieData,
                      angleField: 'value',
                      colorField: 'type',
                      radius: 0.8,
                      label: false,
                      sortData: false, // Prevent automatic sorting to maintain color consistency
                      color: colorArray, // Use array to ensure color order matches data order
                      tooltip: {
                        showTitle: true,
                        formatter: (datum: any) => {
                          const name = datum?.type || datum?.name || 'Unknown';
                          const value = datum?.value ?? 0;
                          if (value === 0 || !value) {
                            return {
                              name: name,
                              value: '0.00 ' + (i18n.language === 'en' ? 'SAR' : 'ر.س'),
                            };
                          }
                          const formattedValue = new Intl.NumberFormat('en-US', {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          }).format(value);
                          return {
                            name: name,
                            value: `${formattedValue} ${i18n.language === 'en' ? 'SAR' : 'ر.س'}`,
                          };
                        },
                      },
                      legend: false,
                      interactions: [{ type: 'element-active' }],
                      statistic: {
                        title: false,
                        content: {
                          style: {
                            whiteSpace: 'pre-wrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            fontSize: '16px',
                          },
                          content: `${t('dashboard.totalSales')}: ${salesByRepData.total_sales} ${
                            i18n.language === 'en' ? 'SAR' : 'ر.س'
                          }`,
                        },
                      },
                    };

                    return (
                      <div
                        style={{
                          width: '100%',
                          minHeight: '400px',
                          overflow: 'visible',
                          display: 'flex',
                          flexDirection: 'column',
                        }}
                        className={i18n.language === 'ar' ? 'arabic-chart-wrapper' : ''}
                      >
                        <div style={{ flex: '0 0 auto', overflow: 'hidden', marginBottom: '8px' }}>
                          <Pie
                            key={`sales-by-rep-chart-${pieData.length}-${i18n.language}-${startDate}-${endDate}`}
                            {...config}
                            width={containerWidth - 40}
                            height={320}
                            onReady={(chart) => {
                              if (chart && chart.chart) {
                                try {
                                  const g2Chart = chart.chart;

                                  // Extract actual colors from chart elements after render
                                  setTimeout(() => {
                                    try {
                                      const geometries = g2Chart.geometries;
                                      if (geometries && geometries.length > 0) {
                                        const elements = geometries[0].elements || [];
                                        const extractedColors: { [key: string]: string } = {};

                                        // Extract colors and add labels inside shapes
                                        const coordinate = g2Chart.getCoordinate();
                                        const center = coordinate.getCenter();
                                        const radius = coordinate.getRadius();
                                        const innerRadius = coordinate.innerRadius || 0;
                                        const labelRadius =
                                          innerRadius + (radius - innerRadius) * 0.5;

                                        // Calculate total value for angle calculation
                                        const total = pieData.reduce(
                                          (sum: number, item: any) => sum + (item.value || 0),
                                          0
                                        );
                                        let currentAngle = -Math.PI / 2; // Start from top

                                        pieData.forEach((dataItem: any, index: number) => {
                                          const element = elements[index];
                                          if (element && element.shape && element.shape.attr) {
                                            const fillColor = element.shape.attr('fill');
                                            if (fillColor && dataItem.type) {
                                              extractedColors[dataItem.type] = fillColor;
                                            }

                                            // Calculate angle for this segment
                                            const angle =
                                              ((dataItem.value || 0) / total) * Math.PI * 2;
                                            const midAngle = currentAngle + angle / 2;
                                            currentAngle += angle;

                                            // Calculate label position (center of segment)
                                            const labelX =
                                              center.x + labelRadius * Math.cos(midAngle);
                                            const labelY =
                                              center.y + labelRadius * Math.sin(midAngle);

                                            // Format value
                                            const formattedValue = new Intl.NumberFormat('en-US', {
                                              minimumFractionDigits: 2,
                                              maximumFractionDigits: 2,
                                            }).format(dataItem.value || 0);

                                            // Add label group
                                            const labelGroup = g2Chart.get('canvas').addGroup();

                                            // Add name text
                                            labelGroup.addShape('text', {
                                              attrs: {
                                                x: labelX,
                                                y: labelY - 8,
                                                text: dataItem.type,
                                                fontSize: i18n.language === 'ar' ? 10 : 11,
                                                fontFamily:
                                                  i18n.language === 'ar'
                                                    ? "'Cairo', 'Segoe UI', sans-serif"
                                                    : 'inherit',
                                                fill: '#fff',
                                                fontWeight: 600,
                                                textAlign: 'center',
                                                textBaseline: 'middle',
                                              },
                                            });

                                            // Add value text
                                            labelGroup.addShape('text', {
                                              attrs: {
                                                x: labelX,
                                                y: labelY + 8,
                                                text: `${formattedValue} ${i18n.language === 'en' ? 'SAR' : 'ر.س'}`,
                                                fontSize: i18n.language === 'ar' ? 9 : 10,
                                                fontFamily:
                                                  i18n.language === 'ar'
                                                    ? "'Cairo', 'Segoe UI', sans-serif"
                                                    : 'inherit',
                                                fill: '#fff',
                                                fontWeight: 500,
                                                textAlign: 'center',
                                                textBaseline: 'middle',
                                              },
                                            });
                                          }
                                        });

                                        if (Object.keys(extractedColors).length > 0) {
                                          setActualChartColors(extractedColors);
                                        }
                                      }
                                    } catch (e) {
                                      console.error('Error extracting colors or adding labels:', e);
                                    }
                                  }, 500);
                                } catch (e) {
                                  console.error('Chart ready error:', e);
                                }
                              }
                            }}
                            onError={(error: any) => {
                              console.error('Chart error:', error);
                            }}
                          />
                        </div>
                        {/* Custom Legend */}
                        <div
                          style={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            justifyContent: 'flex-start',
                            alignItems: 'flex-start',
                            direction: i18n.language === 'ar' ? 'rtl' : 'ltr',
                            textAlign: i18n.language === 'ar' ? 'right' : 'left',
                            width: '100%',
                            boxSizing: 'border-box',
                            minHeight: `${legendHeight}px`,
                          }}
                        >
                          {pieData.map((item: any, index: number) => {
                            const formattedValue = new Intl.NumberFormat('en-US', {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            }).format(item.value);
                            const color =
                              actualChartColors[item.type] ||
                              colorMap[item.type] ||
                              defaultColors[index % defaultColors.length];
                            return (
                              <div
                                key={`legend-${index}-${item.type}`}
                                style={{
                                  display: 'flex',
                                  alignItems: 'flex-start',
                                  fontSize: i18n.language === 'ar' ? '11px' : '12px',
                                  fontFamily:
                                    i18n.language === 'ar'
                                      ? "'Cairo', 'Segoe UI', sans-serif"
                                      : 'inherit',
                                  lineHeight: 1.5,
                                  margin: 0,
                                  padding: '4px 6px',
                                  flex: '0 0 auto',
                                  width: 'fit-content',
                                  boxSizing: 'border-box',
                                }}
                              >
                                <span
                                  style={{
                                    display: 'inline-block',
                                    width: '10px',
                                    height: '10px',
                                    borderRadius: '2px',
                                    backgroundColor: color,
                                    marginRight: i18n.language === 'ar' ? '0' : '6px',
                                    marginLeft: i18n.language === 'ar' ? '6px' : '0',
                                    marginTop: '4px',
                                    flexShrink: 0,
                                  }}
                                />
                                <div
                                  style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    direction: i18n.language === 'ar' ? 'rtl' : 'ltr',
                                    textAlign: i18n.language === 'ar' ? 'right' : 'left',
                                    flex: '1 1 auto',
                                    minWidth: 0,
                                  }}
                                >
                                  <span
                                    style={{
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      whiteSpace: 'nowrap',
                                      fontWeight: 600,
                                    }}
                                    title={item.type}
                                  >
                                    {item.type}
                                  </span>
                                  <span
                                    style={{
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      whiteSpace: 'nowrap',
                                      fontSize: i18n.language === 'ar' ? '10px' : '11px',
                                      color: 'var(--color-text-secondary)',
                                      marginTop: '2px',
                                    }}
                                    title={`${formattedValue} ${i18n.language === 'en' ? 'SAR' : 'ر.س'}`}
                                  >
                                    {formattedValue} {i18n.language === 'en' ? 'SAR' : 'ر.س'}
                                  </span>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  } catch (error) {
                    console.error('Error rendering chart:', error);
                    return (
                      <div
                        style={{
                          textAlign: 'center',
                          padding: '40px 20px',
                          color: 'var(--color-text-secondary)',
                        }}
                      >
                        {t('dashboard.chartError') || 'Error loading chart'}
                      </div>
                    );
                  }
                })()}
              </div>
            ) : (
              <EmptyState
                title={t('dashboard.noDataAvailable')}
                description={t('dashboard.salesChart')}
              />
            )}
          </SectionCard>
        )}
      </div>

      {/* View/Export Modal */}
      {canOpenSalesReportModal && (
        <Modal
          title={t('dashboard.salesReport') || 'Sales Report'}
          open={isViewModalVisible}
          onCancel={() => {
            setIsViewModalVisible(false);
            setActiveTab('');
            setRepresentativeDetails({});
          }}
          width={1400}
          footer={[
            <Button
              key="cancel"
              onClick={() => {
                setIsViewModalVisible(false);
                setActiveTab('');
                setRepresentativeDetails({});
              }}
            >
              {t('common.cancel')}
            </Button>,
            canExportDashboardReports && (
              <Button
                key="export"
                type="primary"
                icon={<DownloadOutlined />}
                loading={exportLoading}
                onClick={() => handleExportReport()}
                disabled={selectedRepresentatives.length === 0}
              >
                {t('common.export')}
              </Button>
            ),
          ]}
        >
          {salesByRepData && salesByRepData.sales_by_representatives.length > 0 && (
            <div>
              <div style={{ marginBottom: 16 }}>
                <Checkbox
                  checked={
                    selectedRepresentatives.length ===
                    salesByRepData.sales_by_representatives.length
                  }
                  indeterminate={
                    selectedRepresentatives.length > 0 &&
                    selectedRepresentatives.length < salesByRepData.sales_by_representatives.length
                  }
                  onChange={(e) => handleSelectAll(e.target.checked)}
                >
                  <Text strong>
                    {t('common.selectAll') || 'Select All'} ({selectedRepresentatives.length}/
                    {salesByRepData.sales_by_representatives.length})
                  </Text>
                </Checkbox>
              </div>
              {canViewSalesReports ? (
                <Tabs
                  activeKey={activeTab}
                  onChange={handleTabChange}
                  type="card"
                  items={salesByRepData.sales_by_representatives.map((rep: any) => {
                    const repId = rep.sales_representative_id;
                    const details = representativeDetails[repId] || [];
                    const isLoading = loadingDetails[repId] || false;

                    return {
                      key: String(repId),
                      label: (
                        <span>
                          <Checkbox
                            checked={selectedRepresentatives.includes(repId)}
                            onChange={(e) => {
                              e.stopPropagation();
                              handleSelectRepresentative(repId, e.target.checked);
                            }}
                            onClick={(e) => e.stopPropagation()}
                          />{' '}
                          {rep.sales_representative_name}
                        </span>
                      ),
                      children: (
                        <div>
                          {isLoading ? (
                            <div style={{ textAlign: 'center', padding: '40px' }}>
                              <Spin size="large" />
                            </div>
                          ) : details.length === 0 ? (
                            <div className="dashboard-empty-state">
                              {t('dashboard.noDataAvailable') || 'No data available'}
                            </div>
                          ) : (
                            (() => {
                              return (
                                <Table
                                  dataSource={details}
                                  rowKey="customer_id"
                                  pagination={false}
                                  scroll={{ y: 400 }}
                                  columns={[
                                    {
                                      title: t('dashboard.customer') || 'Customer',
                                      dataIndex: 'customer_name',
                                      key: 'customer_name',
                                      render: (text: string) => <Text strong>{text}</Text>,
                                    },
                                    {
                                      title:
                                        t('dashboard.totalInvoicesAmount') ||
                                        'إجمالي مبالغ الفواتير تشمل ض.ق.م (SAR)',
                                      dataIndex: 'total_invoices_amount',
                                      key: 'total_invoices_amount',
                                      align: 'right' as const,
                                      render: (value: number) => (
                                        <Text>
                                          {new Intl.NumberFormat('en-US', {
                                            minimumFractionDigits: 2,
                                            maximumFractionDigits: 2,
                                          }).format(value)}{' '}
                                          {i18n.language === 'en' ? 'SAR' : 'ر.س'}
                                        </Text>
                                      ),
                                    },
                                    {
                                      title:
                                        t('dashboard.totalPaidAmount') ||
                                        'إجمالي المبالغ المسددة (SAR)',
                                      dataIndex: 'total_paid_amount',
                                      key: 'total_paid_amount',
                                      align: 'right' as const,
                                      render: (value: number) => (
                                        <Text>
                                          {new Intl.NumberFormat('en-US', {
                                            minimumFractionDigits: 2,
                                            maximumFractionDigits: 2,
                                          }).format(value)}{' '}
                                          {i18n.language === 'en' ? 'SAR' : 'ر.س'}
                                        </Text>
                                      ),
                                    },
                                    {
                                      title:
                                        t('dashboard.totalDueAmount') || 'الرصيد المستحق (SAR)',
                                      dataIndex: 'total_due_amount',
                                      key: 'total_due_amount',
                                      align: 'right' as const,
                                      render: (value: number) => (
                                        <Text>
                                          {new Intl.NumberFormat('en-US', {
                                            minimumFractionDigits: 2,
                                            maximumFractionDigits: 2,
                                          }).format(value)}{' '}
                                          {i18n.language === 'en' ? 'SAR' : 'ر.س'}
                                        </Text>
                                      ),
                                    },
                                    {
                                      title: t('dashboard.paymentPercentage') || 'نسبة السداد (%)',
                                      dataIndex: 'payment_percentage',
                                      key: 'payment_percentage',
                                      align: 'right' as const,
                                      render: (_value: number | null, record: any) => (
                                        <SalesRepPaymentPercentage row={record} />
                                      ),
                                    },
                                    {
                                      title:
                                        t('dashboard.totalReceiptsAmount') ||
                                        'إجمالي مبالغ السندات المسددة (SAR)',
                                      dataIndex: 'total_receipts_amount',
                                      key: 'total_receipts_amount',
                                      align: 'right' as const,
                                      render: (value: number) => (
                                        <Text>
                                          {new Intl.NumberFormat('en-US', {
                                            minimumFractionDigits: 2,
                                            maximumFractionDigits: 2,
                                          }).format(value)}{' '}
                                          {i18n.language === 'en' ? 'SAR' : 'ر.س'}
                                        </Text>
                                      ),
                                    },
                                  ]}
                                  summary={(pageData) => {
                                    const rows = pageData as any[];
                                    const customersWithInvoices = rows.filter((item: any) =>
                                      hasInvoicesInPeriod(item)
                                    );
                                    const totalPct = totalPaymentPercentage(rows);
                                    const rowTotals = rows.reduce(
                                      (acc: any, item: any) => {
                                        acc.total_invoices_amount +=
                                          parseFloat(item.total_invoices_amount) || 0;
                                        acc.total_paid_amount +=
                                          parseFloat(item.total_paid_amount) || 0;
                                        acc.total_due_amount +=
                                          parseFloat(item.total_due_amount) || 0;
                                        acc.total_receipts_amount +=
                                          parseFloat(item.total_receipts_amount) || 0;
                                        return acc;
                                      },
                                      {
                                        total_invoices_amount: 0,
                                        total_paid_amount: 0,
                                        total_due_amount: 0,
                                        total_receipts_amount: 0,
                                      }
                                    );

                                    return (
                                      <Table.Summary fixed>
                                        <Table.Summary.Row
                                          style={{
                                            backgroundColor: '#fafafa',
                                            fontWeight: 'bold',
                                          }}
                                        >
                                          <Table.Summary.Cell index={0} align="left">
                                            <Text strong>
                                              {t('dashboard.total') || 'الإجمالي'} (
                                              {customersWithInvoices.length}/{rows.length})
                                            </Text>
                                          </Table.Summary.Cell>
                                          <Table.Summary.Cell index={1} align="right">
                                            <Text strong>
                                              {new Intl.NumberFormat('en-US', {
                                                minimumFractionDigits: 2,
                                                maximumFractionDigits: 2,
                                              }).format(rowTotals.total_invoices_amount)}{' '}
                                              {i18n.language === 'en' ? 'SAR' : 'ر.س'}
                                            </Text>
                                          </Table.Summary.Cell>
                                          <Table.Summary.Cell index={2} align="right">
                                            <Text strong>
                                              {new Intl.NumberFormat('en-US', {
                                                minimumFractionDigits: 2,
                                                maximumFractionDigits: 2,
                                              }).format(rowTotals.total_paid_amount)}{' '}
                                              {i18n.language === 'en' ? 'SAR' : 'ر.س'}
                                            </Text>
                                          </Table.Summary.Cell>
                                          <Table.Summary.Cell index={3} align="right">
                                            <Text strong>
                                              {new Intl.NumberFormat('en-US', {
                                                minimumFractionDigits: 2,
                                                maximumFractionDigits: 2,
                                              }).format(rowTotals.total_due_amount)}{' '}
                                              {i18n.language === 'en' ? 'SAR' : 'ر.س'}
                                            </Text>
                                          </Table.Summary.Cell>
                                          <Table.Summary.Cell index={4} align="right">
                                            <Text strong>{totalPct.toFixed(2)}%</Text>
                                          </Table.Summary.Cell>
                                          <Table.Summary.Cell index={5} align="right">
                                            <Text strong>
                                              {new Intl.NumberFormat('en-US', {
                                                minimumFractionDigits: 2,
                                                maximumFractionDigits: 2,
                                              }).format(rowTotals.total_receipts_amount)}{' '}
                                              {i18n.language === 'en' ? 'SAR' : 'ر.س'}
                                            </Text>
                                          </Table.Summary.Cell>
                                        </Table.Summary.Row>
                                      </Table.Summary>
                                    );
                                  }}
                                />
                              );
                            })()
                          )}
                        </div>
                      ),
                    };
                  })}
                />
              ) : (
                <div style={{ marginTop: 8 }}>
                  {salesByRepData.sales_by_representatives.map((rep: any) => (
                    <div key={rep.sales_representative_id} style={{ marginBottom: 8 }}>
                      <Checkbox
                        checked={selectedRepresentatives.includes(rep.sales_representative_id)}
                        onChange={(e) =>
                          handleSelectRepresentative(rep.sales_representative_id, e.target.checked)
                        }
                      >
                        {rep.sales_representative_name}
                      </Checkbox>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </Modal>
      )}

      <div className="monthly-stats ui-stagger-children">
        {canViewInvoices && (
          <StatCard
            variant="bold"
            tone="info"
            title={t('dashboard.thisMonthInvoices')}
            value={monthlyStats.invoices.current}
            trend={monthlyStats.invoices.change}
            delay={0.1}
          />
        )}
        {canViewReceipts && (
          <StatCard
            variant="bold"
            tone="success"
            title={t('dashboard.thisMonthReceipts')}
            value={monthlyStats.receipts.current}
            trend={monthlyStats.receipts.change}
            delay={0.15}
          />
        )}
        {canViewOrders && (
          <StatCard
            variant="bold"
            tone="warning"
            title={t('dashboard.pendingOrders')}
            value={stats.pendingOrders}
            delay={0.2}
          />
        )}
        {canViewSales && (
          <StatCard
            variant="bold"
            tone="violet"
            title={t('dashboard.totalSales')}
            value={`${stats.totalSales} ${i18n.language === 'en' ? 'SAR' : 'ر.س'}`}
            delay={0.25}
          />
        )}
      </div>
    </AnimatedContainer>
  );
};

export default Dashboard;
